<template>
    <v-card :loading="loading">
        <v-card-title>
            <v-btn small icon @click="close" color="primary" class="ml-n3 mr-2">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <h2 class="font-weight-regular">
                Add Items File
            </h2>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text>
            <v-form v-model="valid" class="mt-2 mb-0">
                <v-row no-gutters>
                    <v-col cols="12" v-if="partNumber">
                        <v-text-field
                            v-model="partNumber"
                            prepend-icon="mdi-barcode"
                            prefix="Part number: "
                            class="mr-xl-2 mr-lg-2 mr-md-2"
                            readonly
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" v-if="partNumber">
                        <v-file-input
                            chips
                            label="File"
                            v-model="file"
                            :rules="[rules.required]"
                            required
                            prepend-icon="mdi-paperclip"
                            accept=".pdf,.dxf,.stp"
                        ></v-file-input>
                    </v-col>
                    <v-col cols="12" v-else>
                        <v-alert dense outlined type="info" class="mb-4">
                            Maximum {{ MAX_WORK_ORDER_ITEMS }} items per work
                            order.
                        </v-alert>
                        <v-file-input
                            chips
                            label="File"
                            v-model="file"
                            :rules="[rules.required]"
                            required
                            prepend-icon="mdi-paperclip"
                            accept=".xls,.xlsx"
                        ></v-file-input>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                rounded
                color="primary"
                class="mb-5 mt-n4"
                @click="selectMethod"
                :disabled="!valid"
                :loading="loading"
            >
                ADD FILES
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import * as XLSX from 'xlsx'
import API from '@/services/api'
import { mapMutations } from 'vuex'
import { saveFile } from '@/services/storage/saveFile.js'
import { isFinishSelectable } from '@/helpers/selectableFinishes.js'
import { MAX_WORK_ORDER_ITEMS } from '@/constants/workOrderLimits'

export default {
    name: 'AddItemFiles',
    props: {
        partNumber: String,
        partNumbers: {
            type: Array,
            required: true,
        },
        workOrder: {
            type: Object,
            required: true,
        },
        selectedItem: {
            type: Object,
            default: () => undefined,
        },
        settingProcesses: {
            type: Object,
            default: () => ({}),
        },
        settingFinish: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        MAX_WORK_ORDER_ITEMS,
        valid: false,
        loading: false,
        file: undefined,
        rules: {
            required: v => !!v || 'The value is required',
        },
        newItems: [],
        dataCSV: undefined,
        sheetData: undefined,
        extensions: 'pdf,dxf,stp',
        companyId: JSON.parse(localStorage.getItem('company')),
        splitChar: '/',
        splitCharElement: ',',
    }),
    methods: {
        ...mapMutations(['setErrorItems']),
        parseNumericValue(value) {
            if (value === undefined || value === null || value === '') {
                return null
            }
            const normalized =
                typeof value === 'string' && value.includes(',')
                    ? value.replace(',', '.')
                    : value
            const parsed = parseFloat(normalized)
            return Number.isFinite(parsed) ? parsed : null
        },
        async selectMethod() {
            if (this.partNumber) {
                await this.addFile()
            } else {
                await this.readCSV()
            }
        },
        async addFile() {
            try {
                this.loading = true
                const lastIndex = this.file.name.lastIndexOf('.')
                if (
                    !this.extensions.includes(
                        this.file.name.split('.').pop()
                    ) ||
                    this.partNumber.toLowerCase() !=
                        this.file.name.substring(0, lastIndex).toLowerCase()
                ) {
                    throw new Error(
                        "The part number doesn't match the file name"
                    )
                }
                await saveFile(
                    this.file,
                    `${this.companyId}/workOrders/${this.workOrder.id}/items`
                )
                const item = this.workOrder.items.find(
                    element =>
                        element.partNumber.toLowerCase() ==
                        this.file.name.substring(0, lastIndex).toLowerCase()
                )
                if (!item.files || item.files.length == 0) {
                    item.files = [this.file.name]
                } else {
                    item.files = [
                        ...item.files.filter(
                            file =>
                                file.toLowerCase() !=
                                this.file.name.toLowerCase()
                        ),
                        this.file.name,
                    ]
                }
                await API.addFilesToItems({
                    workOrderId: this.workOrder.id,
                    items: [
                        { partNumberId: item.partNumberId, files: item.files },
                    ],
                })
                //TODO: partNumbers Refactoring
                this.$emit('addFiles', [
                    { partNumberId: item.partNumberId, files: item.files },
                ])
                this.close()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('close')
        },
        async completeReading(results) {
            try {
                this.loading = true
                this.dataCSV = results
                await this.addFiles()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async readCSV() {
            try {
                this.loading = true
                if (
                    this.file.name.endsWith('.xls') ||
                    this.file.name.endsWith('.xlsx')
                ) {
                    const data = await this.file.arrayBuffer()
                    const workbook = XLSX.read(data)
                    const sheetName = workbook.SheetNames[0]
                    const xlsData = XLSX.utils.sheet_to_json(
                        workbook.Sheets[sheetName],
                        { raw: false }
                    )
                    this.sheetData = xlsData
                    await this.addFiles(workbook)
                } else {
                    throw new Error('Unsupported file format')
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async addFiles(woorkbook) {
            try {
                this.loading = true

                // Validate required columns
                this.validateRequiredColumns()

                let partNumbersNotFound = []
                let items = this.sheetData
                // validate part numbers
                for (const itemIndex in items) {
                    const partNumber = this.partNumbers.find(
                        pn => pn.code == items[itemIndex]['Part Number']
                    )
                    if (!partNumber) {
                        partNumbersNotFound.push(
                            items[itemIndex]['Part Number']
                        )
                    } else if (
                        this.workOrder.items.find(
                            i => i.partNumberId == partNumber.id
                        )
                    ) {
                        items.splice(itemIndex, 1)
                    }
                }
                if (partNumbersNotFound.length > 0) {
                    this.$emit('openPartNumbersNotFound', partNumbersNotFound)
                    return null
                }
                //assembly array init
                const itemsFileUpload = []
                let assemblyItemsToCreate = []
                for (const item of items) {
                    const {
                        'Part Number': partNumber,
                        'Finish & UC Code': finishAndUCCodeName,
                        Description: description,
                        'Mass (Kg)': mass,
                        'Painting Area (m^2)': paintingArea,
                        'Length (mm)': pieceLength,
                        'Width (mm)': pieceWidth,
                        QTY: qty,
                        NOTES: notes,
                        oc,
                        position,
                        ...otherProps
                    } = item
                    const excludedKeys = [
                        'Mass (lbmass)',
                        'PA (mm^2)',
                        'Mass (Kg)',
                        'Painting Area (m^2)',
                        'Length (mm)',
                        'Width (mm)',
                    ]
                    let processes = Object.keys(otherProps).filter(
                        key => !excludedKeys.includes(key)
                    )
                    processes = processes.map(process => process.trim())

                    const { id: partNumberId } = this.partNumbers.find(
                        pn => pn.code == partNumber
                    )
                    const finishIndex = this.settingFinish.finishes.findIndex(
                        fc =>
                            fc.name == finishAndUCCodeName &&
                            isFinishSelectable(fc)
                    )
                    let finishAndUCCode = ''
                    if (finishIndex > -1) {
                        finishAndUCCode = this.settingFinish.finishes[
                            finishIndex
                        ].id
                    } else {
                        throw new Error(
                            `${partNumber} does not have a valid Finish & UC Code`
                        )
                    }
                    if (this.workOrder.assemblyWorkOrder) {
                        assemblyItemsToCreate.push(item)
                    } else {
                        const { code } = this.partNumbers.find(
                            pn => pn.id == partNumberId
                        )
                        const itemFileUpload = {
                            partNumberId,
                            finishAndUCCode: finishAndUCCode,
                            description,
                            mass: this.parseNumericValue(mass),
                            paintingArea:
                                this.parseNumericValue(paintingArea) || 0,
                            ...(this.parseNumericValue(pieceLength) != null
                                ? {
                                      pieceLength: this.parseNumericValue(
                                          pieceLength
                                      ),
                                  }
                                : {}),
                            ...(this.parseNumericValue(pieceWidth) != null
                                ? {
                                      pieceWidth: this.parseNumericValue(
                                          pieceWidth
                                      ),
                                  }
                                : {}),
                            processes: processes.reduce((ids, processName) => {
                                const match = this.settingProcesses.processes.find(
                                    p =>
                                        p.name.toLowerCase() ===
                                        processName.toLowerCase()
                                )
                                if (match) ids.push(match.id)
                                return ids
                            }, []),
                            notes,
                            quantity: Number(qty),
                        }
                        if (oc !== undefined && position !== undefined) {
                            itemFileUpload.oc = oc
                            itemFileUpload.costPosition = position
                        } else if ((!oc && position) || (!position && oc)) {
                            this.close()
                            throw new Error(
                                `${code} does not have a oc or position`
                            )
                        } else if (oc == undefined && position == undefined) {
                            itemFileUpload.oc = null
                            itemFileUpload.costPosition = null
                        }
                        itemsFileUpload.push(itemFileUpload)
                    }
                }
                if (itemsFileUpload.length > 0) {
                    const existingItemsCount = Object.keys(
                        this.workOrder.partNumbers || {}
                    ).length
                    if (
                        existingItemsCount + itemsFileUpload.length >
                        MAX_WORK_ORDER_ITEMS
                    ) {
                        throw new Error(
                            `The work order has reached the maximum number of items (${MAX_WORK_ORDER_ITEMS}).`
                        )
                    }
                    // Use batch creation
                    const newItems = await API.createMultipleWorkOrderItems({
                        companyId: this.companyId,
                        workOrderId: this.workOrder.id,
                        settingFinishId: this.settingFinish.id,
                        settingProcessesId: this.settingProcesses.id,
                        items: itemsFileUpload,
                    })
                    this.newItems.push(...newItems)
                }

                if (assemblyItemsToCreate.length > 0) {
                    await this.createAssemblyWorkOrder(woorkbook)
                }
                this.$emit('addItems', this.newItems)
                this.close()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        validateRequiredColumns() {
            if (!this.sheetData || this.sheetData.length === 0) {
                throw new Error('The file is empty or has no data')
            }

            const requiredColumns = [
                'Part Number',
                'Finish & UC Code',
                'Description',
                'Mass (Kg)',
                'Painting Area (m^2)',
                'QTY',
            ]

            const firstRow = this.sheetData[0]
            const availableColumns = Object.keys(firstRow)
            const missingColumns = requiredColumns.filter(
                column => !availableColumns.includes(column)
            )

            if (missingColumns.length > 0) {
                throw new Error(
                    `Missing required columns: ${missingColumns.join(', ')}. ` +
                        `Available columns: ${availableColumns.join(', ')}`
                )
            }

            // Validate that required columns have data and correct format
            const validationErrors = []

            for (let i = 0; i < this.sheetData.length; i++) {
                const row = this.sheetData[i]
                const rowNumber = i + 2 // +2 because Excel starts at 1 and we skip header

                // Check for empty required fields
                for (const column of requiredColumns) {
                    if (!row[column] || row[column].toString().trim() === '') {
                        validationErrors.push(
                            `Row ${rowNumber}: ${column} is required but empty`
                        )
                    }
                }

                // Validate numeric fields
                if (
                    row['Mass (Kg)'] &&
                    isNaN(parseFloat(row['Mass (Kg)']))
                ) {
                    validationErrors.push(
                        `Row ${rowNumber}: Mass (Kg) must be a number (current value: "${row['Mass (Kg)']}")`
                    )
                }

                if (
                    row['Painting Area (m^2)'] &&
                    isNaN(parseFloat(row['Painting Area (m^2)']))
                ) {
                    validationErrors.push(
                        `Row ${rowNumber}: Painting Area (m^2) must be a number (current value: "${row['Painting Area (m^2)']}")`
                    )
                }

                if (
                    row['Length (mm)'] &&
                    row['Length (mm)'].toString().trim() !== '' &&
                    isNaN(parseFloat(row['Length (mm)']))
                ) {
                    validationErrors.push(
                        `Row ${rowNumber}: Length (mm) must be a number (current value: "${row['Length (mm)']}")`
                    )
                }

                if (
                    row['Width (mm)'] &&
                    row['Width (mm)'].toString().trim() !== '' &&
                    isNaN(parseFloat(row['Width (mm)']))
                ) {
                    validationErrors.push(
                        `Row ${rowNumber}: Width (mm) must be a number (current value: "${row['Width (mm)']}")`
                    )
                }

                if (
                    row['QTY'] &&
                    (isNaN(parseInt(row['QTY'])) || parseInt(row['QTY']) <= 0)
                ) {
                    validationErrors.push(
                        `Row ${rowNumber}: QTY must be a positive number (current value: "${row['QTY']}")`
                    )
                }
            }

            if (validationErrors.length > 0) {
                const errorMessage = validationErrors.slice(0, 10).join('\n') // Show first 10 errors
                const moreErrors =
                    validationErrors.length > 10
                        ? `\n... and ${validationErrors.length -
                              10} more errors`
                        : ''
                throw new Error(
                    `File validation failed:\n${errorMessage}${moreErrors}`
                )
            }
        },
        async createAssemblyWorkOrder(workbook) {
            try {
                // Validate required columns for assembly work orders
                this.validateRequiredColumns()

                const sheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[sheetName]

                let jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                })

                let headers = jsonData[0]
                let emptyColumnCount = 0
                let dynamicIndex = 0

                for (let i = 0; i < headers.length; i++) {
                    if (emptyColumnCount > 0) {
                        dynamicIndex++
                        headers[i] = `Dynamic_Column_${dynamicIndex}`
                    }
                    if (!headers[i]) {
                        emptyColumnCount++
                    }
                }

                jsonData[0] = headers

                const updatedWorksheet = XLSX.utils.aoa_to_sheet(jsonData)
                workbook.Sheets[sheetName] = updatedWorksheet

                const structuredData = XLSX.utils.sheet_to_json(
                    workbook.Sheets[sheetName]
                )

                const itemsToCreate = []

                for (const item of structuredData) {
                    const {
                        'Part Number': partNumber,
                        'Finish & UC Code': finishAndUCCodeName,
                        Description: description,
                        'Mass (Kg)': mass,
                        'Painting Area (m^2)': paintingArea,
                        'Length (mm)': pieceLength,
                        'Width (mm)': pieceWidth,
                        QTY: qty,
                        NOTES: notes,
                        oc,
                        position,
                        ...otherProps
                    } = item

                    const excludedKeys = [
                        'Mass (lbmass)',
                        'PA (mm^2)',
                        'Mass (Kg)',
                        'Painting Area (m^2)',
                        'Length (mm)',
                        'Width (mm)',
                    ]
                    let processes = Object.keys(otherProps).filter(
                        key => !excludedKeys.includes(key)
                    )
                    processes = processes.map(process => process.trim())

                    const { id: partNumberId } = this.partNumbers.find(
                        pn => pn.code == partNumber
                    )

                    const finishIndex = this.settingFinish.finishes.findIndex(
                        fc =>
                            fc.name == finishAndUCCodeName &&
                            isFinishSelectable(fc)
                    )
                    let finishAndUCCode = ''
                    if (finishIndex > -1) {
                        finishAndUCCode = this.settingFinish.finishes[
                            finishIndex
                        ].id
                    } else {
                        throw new Error(
                            `${partNumber} does not have a valid Finish & UC Code`
                        )
                    }
                    const { code } = this.partNumbers.find(
                        pn => pn.id == partNumberId
                    )
                    const newItem = {
                        partNumberId,
                        finishAndUCCode,
                        description,
                        mass: mass ? this.parseNumericValue(mass) || 0 : 0,
                        paintingArea: paintingArea
                            ? this.parseNumericValue(paintingArea) || 0
                            : 0,
                        ...(pieceLength
                            ? {
                                  pieceLength: this.parseNumericValue(
                                      pieceLength
                                  ),
                              }
                            : {}),
                        ...(pieceWidth
                            ? {
                                  pieceWidth: this.parseNumericValue(
                                      pieceWidth
                                  ),
                              }
                            : {}),
                        processes: processes.reduce((ids, processName) => {
                            const match = this.settingProcesses.processes.find(
                                p =>
                                    p.name.toLowerCase() ===
                                    processName.toLowerCase()
                            )
                            if (match) ids.push(match.id)
                            return ids
                        }, []),
                        notes,
                        quantity: Number(qty),
                    }

                    if (oc !== undefined && position !== undefined) {
                        newItem.oc = oc
                        newItem.costPosition = position
                    } else if ((!oc && position) || (!position && oc)) {
                        throw new Error(
                            `${code} does not have an oc or position`
                        )
                    } else if (oc == undefined && position == undefined) {
                        newItem.oc = null
                        newItem.costPosition = null
                    }
                    let assemblyItems = []
                    let index = 1

                    while (otherProps[`Dynamic_Column_${index}`]) {
                        const partNumber = otherProps[`Dynamic_Column_${index}`]
                        const qtyPerAssembly =
                            otherProps[`Dynamic_Column_${index + 1}`]

                        if (partNumber && qtyPerAssembly) {
                            assemblyItems.push({
                                partNumber,
                                qtyPerAssembly: Number(qtyPerAssembly),
                                mass: newItem.mass,
                                paintingArea: newItem.paintingArea,
                            })
                        }
                        index += 2
                    }
                    newItem.assemblyItems = assemblyItems
                    itemsToCreate.push(newItem)
                }
                let partNumbersToCreate = {}
                itemsToCreate.forEach(item => {
                    partNumbersToCreate[item.partNumberId] = item
                })
                const existingItemsCount = Object.keys(
                    this.workOrder.partNumbers || {}
                ).length
                const newItemsCount = Object.keys(partNumbersToCreate).length
                if (
                    existingItemsCount + newItemsCount >
                    MAX_WORK_ORDER_ITEMS
                ) {
                    throw new Error(
                        `The work order has reached the maximum number of items (${MAX_WORK_ORDER_ITEMS}).`
                    )
                }
                const paramsToCreate = {
                    projectId: this.workOrder.projectId,
                    workOrderId: this.workOrder.id,
                    settingProcessesId: this.settingProcesses.id,
                    settingFinishId: this.settingFinish.id,
                    partNumbers: partNumbersToCreate,
                }
                const assemblyWO = await API.createAssemblyWO(paramsToCreate)
                return assemblyWO
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
    },
}
</script>

<style></style>
